<template>
  <component :is="componentType" class="app-button"
          :class="{
             'app-button--x-small': size === 'x-small',
             'app-button--small': size === 'small',
             'app-button--large': size === 'large',
             'app-button--medium': size === 'medium',
             'app-button--primary': type === 'primary',
             'app-button--outlined': type === 'outlined',
             'app-button--tonal': type === 'tonal',
             'app-button--ghost': type === 'ghost',
             'app-button--icon': type === 'icon',
             'app-button--full-width': fullWidth,
             'app-button--brand': variant === 'brand',
             'app-button--project': variant === 'project',
             'app-button--dataroom': variant === 'dataroom',
             'app-button--archive': variant === 'archive',
             'app-button--neutral': variant === 'neutral',
             'app-button--danger': variant === 'danger',
             'app-button--is-loading': loading,
          }"
          type="button"
          :href="href ? href : null"
          v-bind="$attrs"
          :to="to"
  >
    <span class="d-flex align-center column-gap-2" :style="{ visibility: contentVisibility }">
      <!-- @slot for left icon -->
      <div v-if="$slots['left-icon']" class="left-icon">
        <slot name="left-icon"></slot>
      </div>
      <!-- @slot for button text -->
      <slot></slot>
      <!-- @slot for right icon -->
      <div v-if="$slots['right-icon']" class="right-icon">
        <slot name="right-icon"></slot>
      </div>
    </span>
    <v-progress-circular v-show="loading" indeterminate width="3" />
  </component>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

export type AppButtonSize = 'x-small' | 'small' | 'medium' | 'large'
export type AppButtonType = 'primary' | 'outlined' | 'tonal' | 'ghost' | 'icon'
export type AppButtonVariant = 'brand' | 'project' | 'dataroom' | 'archive' | 'neutral' | 'danger'

/**
 * @displayName Principal Button
 */
export default defineComponent({
  name: 'AppButton',
  inheritAttrs: false,
  props: {
    /**
     * Size of the button
     * Possible values are : small, medium
     */
    size: {
      type: String as PropType<AppButtonSize>,
      default: 'medium',
    },
    /**
     * Hierarchy of the button
     * Possible values are : primary, outlined, tonal, icon
     */
    type: {
      type: String as PropType<AppButtonType>,
      default: 'primary',
    },
    /**
     * Color variant
     * Possible values are : brand, project, dataroom, archive, neutral, danger
     */
    variant: {
      type: String as PropType<AppButtonVariant>,
      default: 'brand',
    },
    /**
     * Specifies whether the button should fill parent's width
     */
    fullWidth: {
      type: Boolean,
      default: false,
    },
    /**
     * If true, replace button text with a loader and disable pointer events
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Link object
     */
    to: {
      type: Object as PropType<object | null>,
      default: null,
    },
    href: {
      type: String,
      default: '',
    },
  },
  computed: {
    contentVisibility () {
      return this.loading ? 'hidden' : 'visible'
    },
    componentType (): string {
      if (this.to) {
        return 'router-link'
      } else if (this.href) {
        return 'a'
      }
      return 'button'
    },
  },
})
</script>

<style scoped lang="scss">
/**
  Note: this is likely what we want everywhere else, since a button is usually inline
  But this is an additive change instead of changing everything else everywhere
*/
a.app-button {
  display: inline-flex;
}

.app-button {
  border-radius: 4px;
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;

  &:disabled {
    cursor: default;
  }

  &.app-button--is-loading {
    pointer-events: none;
  }

  & .v-progress-circular {
    height: 22px !important;
    width: 22px !important;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  &.app-button--full-width {
    width: 100%;
  }

  &.app-button--small {
    min-height: 28px;
    padding: 5px 8px;
    font-size: 14px;
    line-height: 14px;
    font-weight: 500;
    column-gap: 4px;

    & .v-progress-circular {
      height: 12px !important;
      width: 12px !important;
    }
  }

  &.app-button--medium {
    min-height: 40px;
    padding: 8px 16px;
    font-size: 14px;
    line-height: normal;
    font-weight: 500;
    column-gap: 8px;
  }

  &.app-button--primary {
    &.app-button--brand {
      background-color: rgb(var(--v-theme-primary));
      color: rgb(var(--v-theme-white));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-primary-darken-1));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-primary-lighten-2));
      }
    }
    &.app-button--project {
      background-color: rgb(var(--v-theme-project));
      color: rgb(var(--v-theme-white));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-project-darken-1));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-project-lighten-2));
      }
    }
    &.app-button--dataroom {
      background-color: rgb(var(--v-theme-dataroom));
      color: rgb(var(--v-theme-white));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-dataroom-darken-1));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-dataroom-lighten-2));
      }
    }
    &.app-button--archive {
      background-color: rgb(var(--v-theme-archive));
      color: rgb(var(--v-theme-white));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-archive-darken-1));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-archive-lighten-2));
      }
    }
    &.app-button--danger {
      color: rgb(var(--v-theme-white));
      outline-color: rgb(var(--v-theme-error));
      background-color: rgb(var(--v-theme-error));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-error-darken-1));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-error-darken-2));
      }
    }
    &:disabled {
      background-color: rgb(var(--v-theme-grey-50));
      color: rgb(var(--v-theme-grey-300));
    }
  }

  &.app-button--outlined {
    background-color: rgb(var(--v-theme-white));
    color: rgb(var(--v-theme-primary));
    outline: 1px solid transparent;
    outline-offset: -1px;

    &.app-button--brand {
      color: rgb(var(--v-theme-primary));
      outline-color: rgb(var(--v-theme-primary));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-primary-lighten-5));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-primary-lighten-5));
        color: rgb(var(--v-theme-primary-lighten-2));
      }
    }
    &.app-button--project {
      color: rgb(var(--v-theme-project));
      outline-color: rgb(var(--v-theme-project));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-project-lighten-5));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-project-lighten-5));
        color: rgb(var(--v-theme-project-lighten-2));
      }
    }
    &.app-button--dataroom {
      color: rgb(var(--v-theme-dataroom));
      outline-color: rgb(var(--v-theme-dataroom));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-dataroom-lighten-5));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-dataroom-lighten-5));
        color: rgb(var(--v-theme-dataroom-lighten-2));
      }
    }
    &.app-button--archive {
      color: rgb(var(--v-theme-archive));
      outline-color: rgb(var(--v-theme-archive));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-archive-lighten-5));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-archive-lighten-5));
        color: rgb(var(--v-theme-archive-lighten-2));
      }
    }
    &.app-button--neutral {
      color: rgb(var(--v-theme-grey-700));
      outline-color: rgb(var(--v-theme-grey-300));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-grey-50));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-grey-50));
        color: rgb(var(--v-theme-grey-400));
      }
    }
    &.app-button--danger {
      color: rgb(var(--v-theme-error));
      outline-color: rgb(var(--v-theme-error));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-error-lighten-1));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-white));
        color: rgb(var(--v-theme-error))
      }
    }

    &:disabled {
      color: rgb(var(--v-theme-grey-300));
      outline-color: rgb(var(--v-theme-grey-100));
    }
  }

  &.app-button--tonal {
    border: 1px solid transparent;
    &.app-button--brand {
      color: rgb(var(--v-theme-primary));
      background-color: rgb(var(--v-theme-primary-lighten-5));
      &:hover:enabled {
        background-color: rgb(var(--v-theme-primary-lighten-4));
      }
      &:active:enabled {
        color: rgb(var(--v-theme-primary-lighten-2));
        background-color: rgb(var(--v-theme-primary-lighten-4));
      }
    }
    &.app-button--project {
      color: rgb(var(--v-theme-project));
      background-color: rgb(var(--v-theme-project-lighten-5));
      &:hover:enabled {
        background-color: rgb(var(--v-theme-project-lighten-4));
      }
      &:active:enabled {
        color: rgb(var(--v-theme-project-lighten-2));
        background-color: rgb(var(--v-theme-project-lighten-5));
      }
    }
    &.app-button--dataroom {
      color: rgb(var(--v-theme-dataroom));
      background-color: rgb(var(--v-theme-dataroom-lighten-5));
      &:hover:enabled {
        background-color: rgb(var(--v-theme-dataroom-lighten-4));
      }
      &:active:enabled {
        color: rgb(var(--v-theme-dataroom-lighten-2));
        background-color: rgb(var(--v-theme-dataroom-lighten-5));
      }
    }
    &.app-button--archive {
      color: rgb(var(--v-theme-archive));
      background-color: rgb(var(--v-theme-archive-lighten-5));
      &:hover:enabled {
        background-color: rgb(var(--v-theme-archive-lighten-4));
      }
      &:active:enabled {
        color: rgb(var(--v-theme-archive-lighten-2));
        background-color: rgb(var(--v-theme-archive-lighten-5));
      }
    }
    &.app-button--neutral {
      color: rgb(var(--v-theme-grey-700));
      background-color: rgb(var(--v-theme-grey-50));
      border-color: rgb(var(--v-theme-grey-300));
      &:hover:enabled {
        border-color: transparent;
        background-color: rgb(var(--v-theme-grey-100));
      }
      &:active:enabled {
        border-color: transparent;
        color: rgb(var(--v-theme-grey-400));
        background-color: rgb(var(--v-theme-grey-100));
      }
    }

    &:disabled {
      color: rgb(var(--v-theme-grey-300));
      background-color: rgb(var(--v-theme-grey-50));
    }
  }

  &.app-button--ghost {
    background-color: transparent;
    color: rgb(var(--v-theme-grey-700));
    border-radius: 4px;

    &:hover:enabled {
      background-color: rgb(var(--v-theme-grey-50));
    }

    &.--is-active {
      background-color: rgb(var(--v-theme-grey-100)) !important;
    }

    &:active:enabled {
      background-color: rgb(var(--v-theme-grey-50));
      color: rgb(var(--v-theme-grey-400));
    }

    &.app-button--danger {
      color: rgb(var(--v-theme-error));
      outline-color: rgb(var(--v-theme-error));

      &:hover:enabled {
        background-color: rgb(var(--v-theme-error-lighten-1));
      }

      &:active:enabled {
        background-color: rgb(var(--v-theme-white));
        color: rgb(var(--v-theme-error))
      }
    }

    &:disabled {
      color: rgb(var(--v-theme-grey-300));
    }
  }

  &.app-button--icon {
    background-color: transparent;
    color: rgb(var(--v-theme-grey-700));
    height: 32px;
    min-height: 32px;
    min-width: 32px;
    padding: 4px;

    &.app-button--x-small {
      height: 20px;
      min-height: 20px;
      min-width: 20px;
      border-radius: 999px;
    }

    &.app-button--small {
      height: 28px;
      min-height: 28px;
      min-width: 28px;
    }

    &.app-button--large {
      height: 40px;
      min-height: 40px;
      min-width: 40px;
    }

    &:hover:enabled {
      background-color: rgb(var(--v-theme-grey-50));
    }

    &.--is-active {
      background-color: rgb(var(--v-theme-grey-100)) !important;
    }

    &:active:enabled {
      color: rgb(var(--v-theme-grey-500));
    }

    &:disabled {
      color: rgb(var(--v-theme-grey-300));
    }

    &.border {
      border: 1px solid rgb(var(--v-theme-grey-300)) !important;
    }
  }
  
  &.app-button--counter {
    height: 28px;
    padding: 4px 8px;

    &.app-button--small {
      padding: 5px;
    }

    &.app-button--medium {
      padding: 5px;
    }
    
    span {
      column-gap: 4px;
    }
  }

  .left-icon,
  .right-icon {
    display: flex;
    
    :deep(svg) {
      color: currentColor !important;
    }
  }

  &.app-button--compact {
    span {
      column-gap: 4px;
    }
    :deep(svg) {
      padding: 2px;
    }
  }
}
</style>
